import { onBeforeUnmount, onMounted, watch, computed, type ComputedRef } from 'vue';
import type { RaumplanungTocFilterTreeNode } from 'atlas-shared';
import type { IndicatorFilterRegistry } from './filterRegistry';
import { createTagFilter, tagFilterId } from './HasTagFilter';

/**
 * Registers one per-leaf filter for each leaf of the filter tree, keeping them
 * in sync with the tree and cleaning them up on change/unmount.
 *
 * Every leaf (identified by its tag path) is associated with a dedicated tag
 * filter whose id is produced by {@link tagFilterId}. This composable centralises
 * the registration/deregistration lifecycle so the UI component does not manage
 * it manually; it is idempotent (registering an already-known id is a no-op).
 */
export function useTreeFilterRegistration(
    registry: IndicatorFilterRegistry,
    treeSource: ComputedRef<Array<RaumplanungTocFilterTreeNode>> | (() => Array<RaumplanungTocFilterTreeNode>),
): void {
    const tree = typeof treeSource === 'function'
        ? computed(() => treeSource())
        : treeSource

    let registeredLeafIds: string[] = []

    function walk(nodes: RaumplanungTocFilterTreeNode[], inheritedExclusiveGroup?: string): string[] {
        const registered: string[] = []
        for (const node of nodes) {
            // The exclusiveGroup is inherited down the tree: a node's own value
            // (if non-empty) overrides the inherited one for its sub-branches.
            const exclusiveGroup = node.exclusiveGroup?.trim() || inheritedExclusiveGroup
            if (node.children?.length) {
                registered.push(...walk(node.children, exclusiveGroup))
            } else {
                registry.registerFilter(createTagFilter(node.tag, exclusiveGroup))
                registered.push(tagFilterId(node.tag))
            }
        }
        return registered
    }

    function refresh() {
        for (const id of registeredLeafIds) {
            registry.unregisterFilter(id)
        }
        registeredLeafIds = walk(tree.value)
    }

    watch(tree, refresh)
    onMounted(refresh)
    onBeforeUnmount(() => {
        for (const id of registeredLeafIds) {
            registry.unregisterFilter(id)
        }
    })
}